import { createContext, useContext, useCallback, useState, useRef } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';

/* ── Types ─────────────────────────────────────────────────────────── */
type ToastVariant = 'success' | 'error' | 'info';

interface ToastItem {
  id: string;
  title?: string;
  description: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  toast: (opts: { title?: string; description: string; variant?: ToastVariant }) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/* ── Variant styles ────────────────────────────────────────────────── */
const variantStyles: Record<ToastVariant, string> = {
  success:
    'border-green-500/30 bg-green-50 text-green-900 dark:bg-green-950/60 dark:text-green-100 dark:border-green-500/40',
  error:
    'border-red-500/30 bg-red-50 text-red-900 dark:bg-red-950/60 dark:text-red-100 dark:border-red-500/40',
  info: 'border-primary/30 bg-primary/5 text-foreground dark:border-primary/40 dark:bg-primary/10',
};

/* ── Provider ──────────────────────────────────────────────────────── */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counter = useRef(0);

  const toast = useCallback(
    ({
      title,
      description,
      variant = 'info',
    }: {
      title?: string;
      description: string;
      variant?: ToastVariant;
    }) => {
      const id = `toast-${++counter.current}`;
      setToasts((prev) => [...prev, { id, title, description, variant }]);
    },
    [],
  );

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
        {children}

        {toasts.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            className={`group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-lg border p-4 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full sm:data-[state=open]:slide-in-from-bottom-full ${variantStyles[t.variant]}`}
            onOpenChange={(open) => {
              if (!open) dismiss(t.id);
            }}
          >
            <div className="flex-1">
              {t.title && (
                <ToastPrimitive.Title className="text-sm font-semibold">
                  {t.title}
                </ToastPrimitive.Title>
              )}
              <ToastPrimitive.Description className="text-sm opacity-90">
                {t.description}
              </ToastPrimitive.Description>
            </div>
            <ToastPrimitive.Close
              className="inline-flex shrink-0 items-center justify-center rounded-md p-1 opacity-50 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}

        <ToastPrimitive.Viewport className="fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:max-w-[420px]" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

/* ── Hook ──────────────────────────────────────────────────────────── */
// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
